import React, { useState, useEffect, useContext } from "react";
import {
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
} from "react-native";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebase";
import * as Print from "expo-print";
import html2pdf from "html2pdf.js"; // only used on web
import * as Sharing from "expo-sharing";
import { AuthContext } from "../Context/AuthContext";
import { Ionicons } from "@expo/vector-icons";
import * as FileSystem from "expo-file-system";
import { Asset } from "expo-asset";

const DownloadReport = ({
  iconSize = 30,
  iconColor = "black",
  topic,
  selectedQuarter,
}) => {
  const [latestResponse, setLatestResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const { companyId } = useContext(AuthContext);
  const [logoBase64, setLogoBase64] = useState(null);
  
  const getCurrentQuarter = () => {
    const month = new Date().getMonth() + 1;
    if (month <= 3) return "Q1";
    if (month <= 6) return "Q2";
    if (month <= 9) return "Q3";
    return "Q4";
  };

  useEffect(() => {
    if (topic) {
      const quarterToUse = selectedQuarter || getCurrentQuarter();
      fetchLatestResponse(topic, quarterToUse);
    } else {
      setLoading(false);
    }
  }, [topic, selectedQuarter]);

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      showAlert(title, message);
    }
  };
  const fetchLatestResponse = async (topic, quarter) => {
    try {
      const responsesRef = collection(
        db,
        `companies/${companyId}/questionnaireResponses`
      );
      const querySnapshot = await getDocs(responsesRef);

      const filteredResponses = querySnapshot.docs
        .map((doc) => doc.data())
        .filter((data) => data.topic === topic && data.quarter === quarter)
        .sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());

      if (filteredResponses.length > 0) {
        setLatestResponse(filteredResponses[0]);
      } else {
        setLatestResponse(null); // No data for this quarter
      }
    } catch (error) {
      console.error("Error fetching latest response:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchLogoFromDB = async () => {
      try {
        const snapshot = await getDocs(collection(db, "sowin_image"));
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setLogoBase64(data.image); // full data URL
        }
      } catch (err) {
        console.error("Failed to fetch logo:", err);
      }
    };

    fetchLogoFromDB();
  }, []);

  const generatePDF = async () => {
    if (!latestResponse) {
      showAlert(
        `Report not available for ${selectedQuarter}`,
        "Please fill the questionnaire for this quarter."
      );
      return;
    }

    const {
      topic,
      totalScore,
      employeeIdNum,
      userName,
      department,
      companyName,
      pillar,
      timestamp,
      responses,
      NumberOfQuestions,
    } = latestResponse;

    let responseDetailsHtml = "";
    responses.forEach((resp, index) => {
      if (index > 0 && index % 7 === 0) {
        responseDetailsHtml += `<div style="page-break-before: always; padding-top: 40px;"></div>`;
      }
      responseDetailsHtml += `<p><b>Q${index + 1}:</b> ${
        resp.question
      } <br/><b>Selected:</b> ${resp.selectedOption}</p><hr/>`;
    });

    const htmlContent = `
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1, h2 { text-align: center; color: #333; }
          p { font-size: 14px; line-height: 1.5; }
          hr { border: 0; height: 1px; background: #ccc; margin: 10px 0; }
        </style>
      </head>
      <body>
        <div style="padding: 40px;">
          <img src="${logoBase64}" style="width: 100px; position: absolute; top: 20px; right: 20px;" />
          <h1 style="text-align: center; margin-top: 0;">${employeeIdNum} ESG Report</h1>
          <hr/>
          <p><b>Employee ID:</b> ${employeeIdNum}</p>
          <p><b>Name:</b> ${userName}</p>
          <p><b>Department:</b> ${department}</p>
          <p><b>Company:</b> ${companyName}</p>
          <p><b>Topic:</b> ${topic}</p>
          <p><b>Pillar:</b> ${pillar}</p>
          <p><b>Quarter:</b> ${selectedQuarter}</p>
          <p><b>Total Score:</b> ${totalScore} / ${NumberOfQuestions * 5}</p>
          <p><b>Date:</b> ${new Date(timestamp.toDate()).toLocaleString()}</p>
          <h2>Responses</h2>
          ${responseDetailsHtml}
        </div>
      </body>
      </html>
    `;

    if (Platform.OS === "web") {
      // Web-compatible PDF generation using html2pdf
      const element = document.createElement("div");
      element.innerHTML = htmlContent;

      html2pdf()
        .from(element)
        .set({
          margin: 0.5,
          filename: `${employeeIdNum}_${topic}_${selectedQuarter}_ESG_Report.pdf`,
          image: { type: "png", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "A4", orientation: "portrait" },
        })
        .save();
    } else {
      try {
        const { uri } = await Print.printToFileAsync({ html: htmlContent });
        const pdfFileName = `${employeeIdNum}_${topic}_${selectedQuarter}_ESG_Report.pdf`;
        const pdfDest = `${FileSystem.documentDirectory}${pdfFileName}`;
        await FileSystem.moveAsync({ from: uri, to: pdfDest });

        showAlert("PDF Generated", "Report is ready to download", [
          { text: "OK", onPress: () => sharePDF(pdfDest) },
        ]);
      } catch (error) {
        console.error("Error generating PDF:", error);
        showAlert("Failed to generate PDF");
      }
    }
  };

  return (
    <TouchableOpacity onPress={generatePDF} disabled={loading}>
      {loading ? (
        <ActivityIndicator size="small" color={iconColor} />
      ) : (
        <Ionicons name="download-outline" size={iconSize} color={iconColor} />
      )}
    </TouchableOpacity>
  );
};

export default DownloadReport;
